import { useState, useEffect, useCallback } from 'react';
import { digitalTwinApi } from '../../../../Restclient/datacollection';

/**
 * Transforms the /alarms/getTagDeviceStructure response into a flat list of
 * tag items that DTTagTree can render, grouped by device.
 *
 * Input:  { data: { devices: [{ DeviceName, DeviceID, tagdetails: [{ TagID, TagName, PayloadName, Alias, Unit }] }] } }
 * Output: [{ key, tagId, title, payloadName, alias, unit, deviceName, deviceId, isLeaf }]
 */
function transformDeviceTagStructure(response) {
  const devices = response?.data?.devices || response?.devices || [];
  const items = [];
  devices.forEach((device) => {
    (device.tagdetails || []).forEach((tag) => {
      if (!tag.TagID) return;
      items.push({
        key: `${device.DeviceID}_${tag.TagID}`,
        tagId: tag.TagID,
        // title shown in the list — prefer Alias, fall back to TagName
        title: tag.Alias || tag.TagName || tag.TagID,
        payloadName: tag.PayloadName || '',
        alias: tag.Alias || tag.TagName || '',
        unit: tag.Unit || '',
        deviceName: device.DeviceName || '',
        deviceId: device.DeviceID || '',
        gatewayId: device.GatewayID || '',
        isLeaf: true,
      });
    });
  });
  return items;
}

/**
 * Fetches the full device-tag structure for the current project.
 * Returns a flat list of tag items plus a device list for the device picker.
 */
export function useDTTagTree() {
  const [allTags, setAllTags] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTree = useCallback(async () => {
    try {
      setLoading(true);
      const res = await digitalTwinApi.getTagDeviceStructure();
      const tags = transformDeviceTagStructure(res.data);
      setAllTags(tags);

      // Build unique device list (with gateway link) for the device filter picker
      const deviceMap = new Map();
      (res.data?.data?.devices || res.data?.devices || []).forEach((d) => {
        if (d.DeviceID && !deviceMap.has(d.DeviceID)) {
          deviceMap.set(d.DeviceID, {
            DeviceID: d.DeviceID,
            DeviceName: d.DeviceName,
            GatewayID: d.GatewayID || '',
          });
        }
      });
      setDevices(Array.from(deviceMap.values()));
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch tag-device structure');
      setAllTags([]);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  return { allTags, devices, loading, error, refresh: fetchTree };
}
